import { useEffect, useRef } from 'react';
import type { ActionEvent } from '@openuidev/react-lang';
import { StoreProvider, useStore } from './store.tsx';
import { Banner, Button, Composer, Eyebrow, Lockup, Segment, usePicker } from './ds.tsx';
import { Sheets, Thread, useReplyActions } from './blocks.tsx';
import { ExceptionScreen } from './exceptions.tsx';
import { Facilitator } from './Facilitator.tsx';
import { useScreen, type ScreenDef } from './screens.tsx';
import { T, TestChip } from './cards.tsx';
import { W } from './words.ts';

export function App() {
  if (new URLSearchParams(location.search).has('f')) return <Facilitator />;
  return <StoreProvider><Shell /></StoreProvider>;
}

function Shell() {
  const s = useStore();
  const picker = usePicker((f) => s.addPhotos(f));
  const onAction = useReplyActions({ open: picker.open });
  const def = useScreen(picker.open);
  // Joining steps need what came before them; after a reload without it, step back to where the record is.
  useEffect(() => {
    if (!s.loaded || !s.h || s.page) return;
    const m = s.h.membership.state;
    if (s.view >= 4 && s.view <= 5 && !s.records?.purchase && m === 'none') s.setView(s.h.stage === 'preparing' ? 3 : 2);
    else if (s.view >= 6 && m === 'none' && !s.h.payments.length) s.setView(s.records?.purchase ? 4 : 2);
  }, [s.loaded, s.view, s.page, s.h?.membership.state]);
  // Preparation: BOB issues the quote in a few seconds unless the facilitator holds it for the prototype control.
  useEffect(() => {
    if (s.view !== 3 || s.page || s.heldWait || s.exc) return;
    const t = setTimeout(() => s.issue(), 3500);
    return () => clearTimeout(t);
  }, [s.view, s.page, s.heldWait, s.exc]);

  // Each new screen, exception or concept moves focus to its heading, so a screen reader announces the change once.
  const first = useRef(true);
  useEffect(() => {
    if (!s.loaded) return;
    if (first.current) { first.current = false; return; }
    const h = document.querySelector<HTMLElement>('main h1');
    if (h) { h.tabIndex = -1; h.focus({ preventScroll: true }); }
  }, [s.loaded, s.view, s.page, s.exc, s.down, s.mode]);

  return (
    <div className="hc rs-app">
      <Banner />
      <header className="rs-header"><Lockup /><Segment value={s.mode} onChange={s.setMode} /></header>
      {!s.code
        ? <p className="hc-caption">Open this from your research link.</p>
        : !s.loaded ? null
        : s.down || s.exc
          ? <main key={`exc-${s.down ? 'unavailable' : s.exc}`} className="rs-screen rs-screen--exc rs-enter"><ExceptionScreen k={s.down ? 'unavailable' : s.exc!} /></main>
          : <main key={`${s.mode}-${s.view}-${s.page}`} className={`rs-screen rs-screen--${s.mode} rs-enter`}>
              {s.mode === 'ui' ? <UiFrame def={def} /> : <ChatFrame def={def} onAction={onAction} />}
            </main>}
      <Sheets onAction={onAction} />
      <div className="rs-sr" aria-live="polite">{s.announce}</div>
      {picker.input}
    </div>
  );
}

/** The Screens concept: a page per step, the records and direct controls on it, chat one tap away. */
function UiFrame({ def }: { def: ScreenDef }) {
  const s = useStore();
  return (
    <>
      <header className="rs-intro"><div className="rs-wrap"><Eyebrow>{def.eyebrow}</Eyebrow><TestChip /></div><h1 className="rs-h1">{def.title}</h1>{def.lead && <p className="rs-muted rs-lead rs-m0"><T>{def.lead}</T></p>}</header>
      <div className="rs-stack-12">{def.body}</div>
      {s.error && <div className="hc-card rs-error" role="alert"><span className="hc-strong">That didn't go through</span><span className="hc-caption">{s.error}. Nothing changed.</span></div>}
      <div className="rs-actions">
        {def.primary && <Button kind="primary" onClick={def.primary.onClick} disabled={def.primary.disabled}>{def.primary.label}</Button>}
        <Button onClick={() => s.setSheet('ask')}>{W.askQuestion}</Button>
        <div className="rs-row-actions">
          {def.home && <Button kind="quiet" className="rs-auto" onClick={() => s.setView(8)}>{W.back}</Button>}
          {def.controls.map((c) => <Button key={c.label} kind="quiet" className="rs-auto" onClick={c.onClick} disabled={c.disabled}>{c.label}</Button>)}
          <Button kind="quiet" className="rs-auto" onClick={() => s.contact()}>{W.contactBob}</Button>
        </div>
      </div>
    </>
  );
}

/** The Chat concept: the assistant explains beside the same records and controls; the composer is always in reach. */
function ChatFrame({ def, onAction }: { def: ScreenDef; onAction: (a: ActionEvent) => void }) {
  const s = useStore();
  const end = useRef<HTMLDivElement>(null);
  const count = useRef(s.entries.length);
  useEffect(() => { if (s.entries.length > count.current || s.busy) end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); count.current = s.entries.length; }, [s.entries.length, s.busy]);
  const pill = (label: string, go: () => void, primary?: boolean, disabled?: boolean) =>
    <Button key={label} kind={primary ? 'primary' : 'secondary'} className="rs-pill" onClick={go} disabled={disabled}>{label}</Button>;
  return (
    <>
      <h1 className="rs-sr">{def.title}</h1>
      <div className="rs-ai rs-in">
        <span className="hc-caption">{W.assistantLabel}</span>
        <div className="hc-bubble hc-bubble--steward"><span className="hc-strong">{def.title}.</span> <T>{def.intro}</T></div>
      </div>
      <section className="rs-stack-12 rs-panel" aria-label={`${def.eyebrow}: records`}>
        <div className="rs-wrap"><span className="rs-eyebrow">{def.eyebrow}</span><TestChip /></div>
        {def.body}
      </section>
      <Thread onAction={onAction} />
      {s.error && <div className="hc-card rs-error" role="alert"><span className="hc-strong">That didn't go through</span><span className="hc-caption">{s.error}. Nothing changed.</span></div>}
      <div className="rs-pills">
        {def.primary && pill(def.primary.label, def.primary.onClick, true, def.primary.disabled)}
        {def.controls.map((c) => pill(c.label, c.onClick, false, c.disabled))}
        {def.home && pill(W.back, () => s.setView(8))}
      </div>
      <div ref={end} />
      <div className="rs-dock">
        <Composer onSend={(t) => s.turn({ text: t }, t)} disabled={!!s.busy} />
        <div className="rs-row-actions">
          <Button kind="quiet" className="rs-auto" onClick={() => s.contact()}>{W.contactBob}</Button>
        </div>
      </div>
    </>
  );
}
